import { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { useAdminSession, useAdminProducts, useAnalytics } from "@/hooks/useAdmin";
import { CATEGORIES, EMPTY_FORM, productSizes, filterProducts, formToProductPayload, productToForm } from "@/utils/adminHelpers";
import { AdminStats } from "@/components/admin/AdminStats";
import { AdminTabs } from "@/components/admin/AdminTabs";
import { ProductFilters } from "@/components/admin/ProductFilters";
import { ProductGrid } from "@/components/admin/ProductGrid";
import { ProductFormModal } from "@/components/admin/ProductFormModal";
import { ReportsPanel } from "@/components/admin/ReportsPanel";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import { ReferralsPanel } from "@/components/admin/ReferralsPanel";
import { MarkSoldDialog } from "@/components/admin/MarkSoldDialog";

const NO_FILTERS = { search: "", category: "", size: "", brand: "" };

const Spinner = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-[#00FF66] border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function AdminPage() {
  const navigate = useNavigate();
  const { status, logout } = useAdminSession();
  const { products, loading, exchangeRate, fetchProducts, fetchExchangeRate, saveProduct, deleteProduct, markUnsold, toUSD } = useAdminProducts(status === "ok");
  const [activeTab, setActiveTab] = useState("active");
  const analytics = useAnalytics(activeTab === "analytics");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [soldDialogProduct, setSoldDialogProduct] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);

  useEffect(() => {
    if (status === "unauthenticated") navigate("/login");
  }, [status, navigate]);

  const activeProducts = useMemo(() => products.filter(p => !p.sold), [products]);
  const soldProducts = useMemo(() => products.filter(p => p.sold), [products]);
  const tabProducts = activeTab === "active" ? activeProducts : soldProducts;
  const displayProducts = useMemo(() => filterProducts(tabProducts, filters), [tabProducts, filters]);
  const allSizes = useMemo(() => [...new Set(tabProducts.flatMap(p => productSizes(p).map(s => s.toUpperCase())))].sort(), [tabProducts]);
  const allBrands = useMemo(() => [...new Set(tabProducts.map(p => p.brand?.trim()).filter(Boolean))].sort(), [tabProducts]);
  const isProductTab = activeTab === "active" || activeTab === "sold";

  const handleLogout = async () => {
    await logout();
    toast.success("Sesion cerrada");
    navigate("/");
  };

  const openCreate = () => { setEditing(null); setFormData(EMPTY_FORM); setShowForm(true); };
  const openEdit = (product) => { setEditing(product); setFormData(productToForm(product, toUSD)); setShowForm(true); };
  const submitForm = async () => {
    await saveProduct(formToProductPayload(formData), editing?.id);
    setShowForm(false);
  };

  if (status !== "ok" || loading) return <Spinner />;

  const renderTab = () => {
    if (activeTab === "referrals") return <ReferralsPanel />;
    if (activeTab === "analytics") return <AnalyticsPanel data={analytics.data} loading={analytics.loading} onRefresh={analytics.refresh} />;
    if (activeTab === "reports") return <ReportsPanel soldProducts={soldProducts} toUSD={toUSD} />;
    return (
      <ProductGrid
        products={displayProducts}
        totalInTab={tabProducts.length}
        activeTab={activeTab}
        onClearFilters={() => setFilters(NO_FILTERS)}
        onAdd={openCreate}
        onEdit={openEdit}
        onMarkSold={setSoldDialogProduct}
        onMarkUnsold={markUnsold}
        onDelete={deleteProduct}
      />
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="bg-[#050505]/90 backdrop-blur-xl border-b border-white/5 px-4 py-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-oswald font-bold text-xl" data-testid="admin-logo">
              <span className="text-[#00FF66]">O</span><span className="text-white">Y</span><span className="text-[#B026FF]">B</span>
            </Link>
            <span className="text-[#A0A0A0] text-sm">Admin</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-[#A0A0A0] hover:text-white text-sm transition-colors" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <AdminStats products={products} exchangeRate={exchangeRate} toUSD={toUSD} onRefreshRate={fetchExchangeRate} />
        <AdminTabs activeTab={activeTab} onChange={setActiveTab} counts={{ active: activeProducts.length, sold: soldProducts.length }} onAdd={openCreate} />

        {isProductTab && tabProducts.length > 0 && (
          <ProductFilters
            search={filters.search} setSearch={(v) => setFilters(f => ({ ...f, search: v }))}
            category={filters.category} setCategory={(v) => setFilters(f => ({ ...f, category: v }))}
            size={filters.size} setSize={(v) => setFilters(f => ({ ...f, size: v }))}
            brand={filters.brand} setBrand={(v) => setFilters(f => ({ ...f, brand: v }))}
            categories={CATEGORIES} sizes={allSizes} brands={allBrands} resultCount={displayProducts.length}
          />
        )}

        {renderTab()}
      </main>

      {soldDialogProduct && (
        <MarkSoldDialog product={soldDialogProduct} onClose={() => setSoldDialogProduct(null)} onSold={fetchProducts} />
      )}
      {showForm && (
        <ProductFormModal
          formData={formData}
          setFormData={setFormData}
          isEdit={Boolean(editing)}
          exchangeRate={exchangeRate}
          toUSD={toUSD}
          onSubmit={submitForm}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
